import React from 'react';
import {
  ClipboardDocumentListIcon,
  RocketLaunchIcon,
  Squares2X2Icon,
  BookOpenIcon,
  CpuChipIcon,
  ChartBarSquareIcon,
  RectangleStackIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { DASHBOARD_SECTIONS, DashboardSectionId } from '../../pages/dashboard_pages/dashboardSections';

interface Tab {
  id: string;
  name: string;
  icon: React.ElementType;
}

interface CustomerSidebarProps {
  isDashboardView: boolean;
  activeTab: string;
  onTabClick: (tabId: string) => void;
  onBackClick: () => void;
  onCustomerDemoClick?: () => void;
  isCustomerDemoOpen?: boolean;
  onNewProjectClick: () => void;
  isOwner?: boolean; // Add isOwner prop to control access
  isLoading?: boolean; // Add isLoading prop to prevent showing view-only during load
}

const CustomerSidebar: React.FC<CustomerSidebarProps> = ({
  isDashboardView,
  activeTab,
  onTabClick,
  onBackClick,
  onCustomerDemoClick,
  isCustomerDemoOpen = false,
  isOwner = false, // Default to false for safety
  isLoading = false, // Default to false
}) => {

  const dashboardIcons: Record<DashboardSectionId, React.ElementType> = {
    'customer-dashboard': Squares2X2Icon,
    key_goals: ClipboardDocumentListIcon,
    projects: RectangleStackIcon,
    'relationship-map': RectangleStackIcon,
    technology_alignment: RocketLaunchIcon,
    'art-of-the-possible': BookOpenIcon,
    'transformation-graph': PresentationChartLineIcon,
    business_frameworks: Squares2X2Icon,
    critical_application: CpuChipIcon,
  };

  const dashboardTabs: Tab[] = DASHBOARD_SECTIONS.map(({ id, navLabel }) => ({
    id,
    name: navLabel,
    icon: dashboardIcons[id],
  }));

  const editTabs: Tab[] = [
    { id: 'company-details', name: 'Company Details', icon: Squares2X2Icon },
    { id: 'goals', name: 'Goals, Priorities & Industry Trends', icon: ClipboardDocumentListIcon },
    { id: 'projects', name: 'Projects', icon: RectangleStackIcon },
    { id: 'technology', name: 'Technology Alignment', icon: RocketLaunchIcon },
    { id: 'art-of-the-possible', name: 'Art Of Possible', icon: BookOpenIcon },
    { id: 'transformation', name: 'Transformation Maturity', icon: ChartBarSquareIcon },
    { id: 'applications', name: 'Top 10 Critical Applications', icon: CpuChipIcon },
    { id: 'frameworks', name: 'Frameworks', icon: Squares2X2Icon },
  ];

  // Only show edit tabs if user is the owner
  const tabs = isDashboardView ? dashboardTabs : (isOwner ? editTabs : dashboardTabs);
  const navigate = useNavigate();
  const handleModeChange = (nextMode: 'dashboard' | 'edit') => {
    const nextIsDashboard = nextMode === 'dashboard';
    if (nextIsDashboard !== isDashboardView) {
      onBackClick();
    }
  };

  return (
    <header className="z-30 w-full shrink-0 bg-white border-b">
      <div className="px-4 py-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1
              className="whitespace-nowrap text-xl font-bold italic text-[#0F2A4F] cursor-pointer"
              onClick={() => navigate('/')}
            >
              TechProfile <span className="not-italic">2.0</span>
            </h1>

            {!isLoading && !isOwner && (
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-600">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                View-only access
              </span>
            )}
          </div>

          {(isOwner || onCustomerDemoClick) && (
            <div className="inline-flex rounded-md border border-gray-200 bg-gray-100 p-1 shadow-sm" aria-label="Customer navigation">
              <button
                type="button"
                onClick={() => handleModeChange('dashboard')}
                className={`h-9 rounded px-4 text-sm font-medium transition-colors ${
                  isDashboardView && !isCustomerDemoOpen
                    ? 'bg-[#0F2A4F] text-white shadow-sm'
                    : 'text-[#0F2A4F] hover:bg-white'
                }`}
                aria-pressed={isDashboardView && !isCustomerDemoOpen}
              >
                Dashboard
              </button>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => handleModeChange('edit')}
                  className={`h-9 rounded px-4 text-sm font-medium transition-colors ${
                    !isDashboardView && !isCustomerDemoOpen
                      ? 'bg-[#0F2A4F] text-white shadow-sm'
                      : 'text-[#0F2A4F] hover:bg-white'
                  }`}
                  aria-pressed={!isDashboardView && !isCustomerDemoOpen}
                >
                  Edit
                </button>
              )}
              {onCustomerDemoClick && (
                <button
                  type="button"
                  onClick={onCustomerDemoClick}
                  disabled={isLoading}
                  className={`h-9 rounded px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    isCustomerDemoOpen
                      ? 'bg-[#0F2A4F] text-white shadow-sm'
                      : 'text-[#0F2A4F] hover:bg-white'
                  }`}
                  aria-pressed={isCustomerDemoOpen}
                >
                  Demo
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/')}
                className="h-9 rounded px-4 text-sm font-medium text-[#0F2A4F] transition-colors hover:bg-white"
              >
                Home
              </button>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center border-t border-gray-100 py-3">
          <div className="animate-spin rounded-full h-7 w-7 border-t-2 border-b-2 border-[#0F2A4F]"></div>
        </div>
      ) : (
        <nav className="border-t border-slate-300 bg-slate-300 px-0 py-2 shadow-inner" aria-label="Customer sections">
          <ul className="flex gap-2 overflow-x-auto px-4 py-1">
            {tabs.map(({ id, name, icon: Icon }) => (
              <li key={id} className="shrink-0">
                <button
                  type="button"
                  onClick={() => onTabClick(id)}
                  className={`flex h-10 items-center gap-2 whitespace-nowrap rounded-md border px-3 text-sm shadow-sm transition-all duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
                    id === activeTab
                      ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium shadow-md shadow-blue-100'
                      : 'border-gray-200 bg-white text-[#0F2A4F] hover:-translate-y-0.5 hover:border-blue-200 hover:bg-gray-50 hover:shadow-md'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{name}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
};

export default CustomerSidebar;
